import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { BookOpenCheck, GitCompareArrows, ShieldCheck } from 'lucide-react'

import { useAuth } from '@/contexts/AuthContext'
import { rulePacksHttp } from '@/services/rulePacks'
import { toast } from '@/store/toastStore'

const terminalStatuses = new Set(['approved', 'superseded', 'withdrawn'])

export default function RulePackGovernancePage() {
  const queryClient = useQueryClient()
  const { hasPermission } = useAuth()
  const canManage = hasPermission('txh.assessment.rule_pack.manage')
  const canApprove = hasPermission('txh.assessment.rule_pack.approve')
  const [selectedId, setSelectedId] = useState('')
  const [comparisonId, setComparisonId] = useState('')
  const [reason, setReason] = useState('')

  const packsQuery = useQuery({ queryKey: ['rule-packs'], queryFn: rulePacksHttp.list })
  const packs = packsQuery.data ?? []
  const effectiveSelectedId = selectedId || packs[0]?.id || ''
  const detailQuery = useQuery({
    queryKey: ['rule-pack', effectiveSelectedId],
    queryFn: () => rulePacksHttp.get(effectiveSelectedId),
    enabled: Boolean(effectiveSelectedId),
  })
  const comparisonQuery = useQuery({
    queryKey: ['rule-pack-comparison', effectiveSelectedId, comparisonId],
    queryFn: () => rulePacksHttp.compare(effectiveSelectedId, comparisonId),
    enabled: Boolean(effectiveSelectedId && comparisonId && effectiveSelectedId !== comparisonId),
  })
  const detail = detailQuery.data
  const sourceById = useMemo(
    () => new Map(detail?.sources.map(source => [source.id, source]) ?? []),
    [detail?.sources],
  )

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['rule-packs'] })
    await queryClient.invalidateQueries({ queryKey: ['rule-pack', effectiveSelectedId] })
  }
  const validateMutation = useMutation({
    mutationFn: () => rulePacksHttp.validate(effectiveSelectedId),
    onSuccess: async report => {
      toast.success(report.valid ? 'Rule pack is valid' : 'Validation failed', `${report.rule_count} rules checked`)
      await refresh()
    },
    onError: error => toast.error('Validation failed', error instanceof Error ? error.message : 'Please try again.'),
  })
  const decisionMutation = useMutation({
    mutationFn: (action: 'approve' | 'withdraw') => {
      if (!detail) throw new Error('Select a rule pack')
      if (reason.trim().length < 3) throw new Error('Enter a decision reason')
      return action === 'approve'
        ? rulePacksHttp.approve(detail.id, detail.manifest_checksum, reason.trim())
        : rulePacksHttp.withdraw(detail.id, detail.manifest_checksum, reason.trim())
    },
    onSuccess: async updated => {
      toast.success('Rule-pack decision recorded', `${updated.title} is ${updated.status}.`)
      setReason('')
      await refresh()
    },
    onError: error => toast.error('Decision rejected', error instanceof Error ? error.message : 'Please try again.'),
  })

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6">
      <header className="flex items-start gap-3">
        <div className="rounded-lg bg-primary-100 p-3 dark:bg-primary-900/30"><BookOpenCheck className="h-6 w-6 text-primary-600" /></div>
        <div>
          <h1 className="text-2xl font-semibold text-text-primary">Tax Rule Packs</h1>
          <p className="text-sm text-text-secondary">Govern effective-dated law, routing, validation, deadline, and computation rules.</p>
        </div>
      </header>

      {packsQuery.isError && <div className="alert alert-error">Could not load rule packs.</div>}
      {!packsQuery.isLoading && packs.length === 0 && <div className="card p-6">No rule packs are available for this tenant.</div>}

      {packs.length > 0 && (
        <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
          <aside className="card p-3" aria-label="Rule-pack versions">
            {packs.map(pack => (
              <button
                className={`mb-2 w-full rounded-lg border p-3 text-left ${effectiveSelectedId === pack.id ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' : 'border-border'}`}
                key={pack.id}
                onClick={() => setSelectedId(pack.id)}
                type="button"
              >
                <span className="block font-medium text-text-primary">{pack.title}</span>
                <span className="text-xs text-text-secondary">v{pack.semantic_version} · {pack.period_value}</span>
                <span className="mt-2 block text-xs uppercase tracking-wide">{pack.status}</span>
              </button>
            ))}
          </aside>

          {detail && (
            <main className="space-y-5">
              <section className="card space-y-4 p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h2 className="text-xl font-semibold text-text-primary">{detail.title} <span className="text-sm font-normal">v{detail.semantic_version}</span></h2>
                    <p className="text-sm text-text-secondary">{detail.governing_act} · {detail.period_basis} {detail.period_value}</p>
                  </div>
                  <span className="badge badge-primary">{detail.status}</span>
                </div>
                {terminalStatuses.has(detail.status) && (
                  <p className="flex items-center gap-2 text-sm text-text-secondary"><ShieldCheck className="h-4 w-4" />Historical content is immutable. Create a superseding version to change it.</p>
                )}
                <dl className="grid gap-3 text-sm md:grid-cols-2">
                  <div><dt className="text-text-secondary">Manifest SHA-256</dt><dd className="break-all font-mono text-xs">{detail.manifest_checksum}</dd></div>
                  <div><dt className="text-text-secondary">Scope</dt><dd>Individual enabled · HUF disabled</dd></div>
                  <div><dt className="text-text-secondary">Sources</dt><dd>{detail.sources.length}</dd></div>
                  <div><dt className="text-text-secondary">Governed rules</dt><dd>{detail.rules.length}</dd></div>
                </dl>
              </section>

              <section className="card space-y-3 p-5">
                <h3 className="font-semibold text-text-primary">Independent decision</h3>
                <textarea className="input min-h-20 w-full" value={reason} onChange={event => setReason(event.target.value)} placeholder="Record the professional review or withdrawal reason" />
                <div className="flex flex-wrap gap-2">
                  {canManage && !terminalStatuses.has(detail.status) && <button className="btn btn-secondary" onClick={() => validateMutation.mutate()} disabled={validateMutation.isPending}>Validate</button>}
                  {canApprove && detail.status === 'validated' && <button className="btn btn-primary" onClick={() => decisionMutation.mutate('approve')} disabled={decisionMutation.isPending}>Approve</button>}
                  {canApprove && detail.status === 'approved' && <button className="btn btn-danger" onClick={() => decisionMutation.mutate('withdraw')} disabled={decisionMutation.isPending}>Withdraw</button>}
                </div>
              </section>

              <section className="card space-y-3 p-5">
                <h3 className="flex items-center gap-2 font-semibold text-text-primary"><GitCompareArrows className="h-4 w-4" />Compare versions</h3>
                <select className="input w-full" value={comparisonId} onChange={event => setComparisonId(event.target.value)}>
                  <option value="">Choose another version</option>
                  {packs.filter(pack => pack.id !== detail.id).map(pack => <option key={pack.id} value={pack.id}>{pack.title} v{pack.semantic_version}</option>)}
                </select>
                {comparisonQuery.data && (
                  <div className="grid gap-3 text-sm sm:grid-cols-4">
                    <div><strong>Added</strong><div>{comparisonQuery.data.added_rule_keys.join(', ') || 'None'}</div></div>
                    <div><strong>Removed</strong><div>{comparisonQuery.data.removed_rule_keys.join(', ') || 'None'}</div></div>
                    <div><strong>Changed</strong><div>{comparisonQuery.data.changed_rule_keys.join(', ') || 'None'}</div></div>
                    <div><strong>Unchanged</strong><div>{comparisonQuery.data.unchanged_rule_keys.length}</div></div>
                  </div>
                )}
              </section>

              <section className="card space-y-3 p-5">
                <h3 className="font-semibold text-text-primary">Rules and citations</h3>
                {detail.rules.map(rule => (
                  <details className="rounded-lg border border-border p-3" key={rule.id}>
                    <summary className="cursor-pointer font-medium">{rule.rule_key} <span className="text-xs text-text-secondary">{rule.catalog} · {rule.effective_from}{rule.effective_to ? ` to ${rule.effective_to}` : ' onward'}</span></summary>
                    <ul className="mt-3 space-y-2 text-sm">
                      {rule.citations.map(citation => {
                        const source = sourceById.get(citation.source_artifact_id)
                        return <li key={citation.id}><a className="link" href={source?.official_url} target="_blank" rel="noreferrer">{source?.title ?? 'Official source'}</a> — {citation.locator}: {citation.reason}</li>
                      })}
                    </ul>
                  </details>
                ))}
              </section>
            </main>
          )}
        </div>
      )}
    </div>
  )
}
